/**
 * Traduce los rechazos de la base a un mensaje para el usuario.
 *
 * Los errores de RLS llegan crudos de Postgres ("new row violates row-level
 * security policy for table \"entidades_relacionadas\"") y la UI los pintaba tal
 * cual en el toast: el usuario no entiende qué hacer y encima se le muestran
 * nombres de tablas y de policies que no le tocan.
 */

/** Códigos de Postgres que significan "no tienes permiso", no "hay un bug". */
const CODIGOS_PERMISO = new Set(["42501", "PGRST301"]);

type ErrorBd = { code?: string; message?: string };

/** True si el error es un rechazo de permisos/RLS y no una falla técnica. */
export function esErrorDePermiso(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const err = error as ErrorBd;
  if (err.code && CODIGOS_PERMISO.has(err.code)) return true;
  return /row-level security|permission denied/i.test(err.message ?? "");
}

/**
 * Mensaje para el toast. `accion` completa la frase: "No tienes permiso para
 * <accion>." Cuando el error no es de permisos se devuelve el mensaje original,
 * que sí describe un problema real que conviene reportar.
 */
export function mensajeErrorPermiso(error: unknown, accion: string): string {
  if (esErrorDePermiso(error)) {
    return `No tienes permiso para ${accion}. Pídelo a un administrador o a Soporte.`;
  }
  const mensaje = (error as ErrorBd | null)?.message;
  return mensaje && mensaje.trim() ? mensaje : "Ocurrió un error inesperado.";
}

/**
 * Un UPDATE que la RLS no deja tocar NO falla: PostgREST responde 200 con 0
 * filas y la UI decía "Guardado" sin haber guardado nada. Con `.select()` en la
 * escritura, esto convierte las 0 filas en un error de permisos (código 42501),
 * el mismo que ya traducen `esErrorDePermiso` y `mensajeErrorPermiso`.
 *
 * `data` es lo que devolvió la escritura con `.select()`: arreglo de filas o,
 * con `.single()` / `.maybeSingle()`, la fila o null.
 */
export function exigirFilasEscritas(
  data: unknown,
  mensaje = "No tienes permiso para modificar este registro.",
): void {
  const filas = Array.isArray(data) ? data.length : data != null ? 1 : 0;
  if (filas > 0) return;
  throw Object.assign(new Error(mensaje), { code: "42501" });
}
